import { useState, useEffect } from "react";
import MiniCupBanner from "../../components/football/MiniCupBanner";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronDown } from "lucide-react";
import { useFb } from "../../utils/football";
import { useAuth } from "../../contexts/AuthContext";
import FootieIcon, { footieVariant } from "../../components/football/FootieIcon";
import FootballProfileModal from "../../components/football/FootballProfileModal";

// MoTD marker: per-day winner (Today/Yesterday) or cumulative count.
const MotdBadge = ({ e, cumulative }) => {
  if (cumulative) {
    if (!(e.manager_of_day_count > 0)) return null;
    return <span className="shrink-0 text-xs" title="Manager of the Day">🏆{e.manager_of_day_count > 1 ? ` ×${e.manager_of_day_count}` : ""}</span>;
  }
  return e.is_motd ? <span className="shrink-0 text-xs" title="Manager of the Day">🏆</span> : null;
};

const TeamCell = ({ e, cumulative, onOpenProfile }) => {
  const fv = footieVariant(e);
  return (
    <button
      type="button"
      onClick={() => onOpenProfile?.(e)}
      className="flex w-full min-w-0 items-center gap-1.5 text-left"
      data-testid={`lb-open-profile-${e.rank}`}
    >
      <span className="min-w-0 truncate text-sm font-bold underline-offset-2 hover:underline">{e.team_name || e.display_name}</span>
      {fv && <FootieIcon size={15} variant={fv} className="shrink-0" />}
      <MotdBadge e={e} cumulative={cumulative} />
      {e.is_current_user && <span className="shrink-0 text-[10px] wc-gold">(you)</span>}
    </button>
  );
};

// 5-column row: [rank] Team | Match | Bonus+Pred | Total
const StatRow = ({ e, cumulative, onOpenProfile }) => (
  <div className={`grid grid-cols-12 items-center gap-1 rounded-lg px-2 py-2.5 ${e.is_current_user ? "border border-amber-400/40 bg-amber-400/10" : ""}`} data-testid={`lb-row-${e.rank}`}>
    <span className="col-span-1 text-center text-xs font-extrabold text-muted-foreground">{e.rank}</span>
    <div className="col-span-4 min-w-0"><TeamCell e={e} cumulative={cumulative} onOpenProfile={onOpenProfile} /></div>
    <span className="col-span-2 text-right text-xs text-muted-foreground" data-testid="lb-match">{e.match_points || 0}</span>
    <span className="col-span-2 text-right text-xs text-muted-foreground" data-testid="lb-bonus-pred">{Math.round(((e.bonus_points || 0) + (e.predictor_points || 0)) * 10) / 10}</span>
    <span className="col-span-3 text-right text-sm font-extrabold wc-gold" data-testid="lb-total">{e.total_points ?? e.points}</span>
  </div>
);

// Predictor tab keeps its specialised layout (accuracy, predictor points only).
const PredictorRow = ({ e, onOpenProfile }) => (
  <div className={`flex items-center justify-between rounded-lg px-3 py-2.5 ${e.is_current_user ? "border border-amber-400/40 bg-amber-400/10" : ""}`} data-testid={`lb-row-${e.rank}`}>
    <button type="button" onClick={() => onOpenProfile?.(e)} className="flex min-w-0 items-center gap-3 text-left" data-testid={`lb-open-profile-${e.rank}`}>
      <span className="w-6 text-center font-extrabold text-muted-foreground">{e.rank}</span>
      <div className="min-w-0">
        <div className="truncate text-sm font-bold underline-offset-2 hover:underline">{e.team_name || e.display_name}{e.is_current_user && <span className="ml-1 text-xs wc-gold">(you)</span>}</div>
        <div className="truncate text-xs text-muted-foreground">{e.display_name}{e.answered > 0 && <span className="ml-1">· {e.accuracy}% acc</span>}</div>
      </div>
    </button>
    <span className="font-extrabold wc-gold">{e.points}</span>
  </div>
);

const OTHER_TABS = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["predictor", "Predictor"],
];

export default function FootballLeaderboardPage() {
  const fb = useFb();
  const navigate = useNavigate();
  const { dbUser } = useAuth();
  const [tab, setTab] = useState("cumulative");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  // Weekly scope for the first tab: "overall" | weekId | "unassigned".
  const [scope, setScope] = useState("overall");
  const [weeks, setWeeks] = useState([]);
  const [scopeOpen, setScopeOpen] = useState(false);

  const isPredictor = tab === "predictor";
  const isCumulative = tab === "cumulative";
  const scopeLabel = scope === "overall" ? "Overall" : (weeks.find((w) => w.id === scope)?.week_name || "Overall");

  const selectScope = (s) => { setScope(s); setTab("cumulative"); setScopeOpen(false); };

  // Load the list of countries that have at least one ranked football user.
  useEffect(() => {
    if (!dbUser) return;
    (async () => {
      try {
        const d = await fb.get("/football/leaderboard/countries");
        setCountries(d.countries || []);
      } catch { setCountries([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  // Load weeks; default the scope to the week containing today's date (if any).
  useEffect(() => {
    if (!dbUser) return;
    (async () => {
      try {
        const d = await fb.get("/football/leaderboard/weeks");
        const ws = d.weeks || [];
        setWeeks(ws);
        const today = new Date().toISOString().slice(0, 10);
        const cur = ws.find((w) => (w.match_days || []).includes(today));
        if (cur) { setScope(cur.id); setTab("cumulative"); }
      } catch { setWeeks([]); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dbUser]);

  useEffect(() => {
    if (!dbUser) return;
    (async () => {
      setLoading(true);
      try {
        if (isCumulative) {
          const params = {};
          if (country) params.country = country;
          if (scope === "overall") {
            const d = await fb.get("/football/leaderboard/cumulative", params);
            setEntries(d.entries || []);
          } else {
            const d = await fb.get(`/football/leaderboard/week/${scope}`, params);
            setEntries(d.entries || []);
          }
        } else if (isPredictor) {
          const d = await fb.get("/football/leaderboard/predictor", { limit: 50, offset: 0 });
          setEntries(d.entries || []);
        } else {
          const params = {};
          if (country) params.country = country;
          const d = await fb.get(`/football/leaderboard/match-day/${tab}`, params);
          setEntries(d.entries || []);
        }
      } catch { setEntries([]); }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, scope, country, dbUser]);

  const emptyText = isPredictor
    ? "No predictions scored yet."
    : tab === "yesterday" ? "No match day yesterday." : tab === "today" ? "No locked squads yet today." : "No rankings yet.";

  return (
    <div className="football-theme min-h-screen bg-background pb-12" data-testid="leaderboard-page">
      <div className="px-4 pt-3"><MiniCupBanner /></div>
      <div className="wc-hero-gradient px-4 pb-5 pt-4">
        <button onClick={() => navigate("/")} className="mb-2 inline-flex items-center gap-1 text-sm text-white/80" data-testid="lb-back"><ChevronLeft size={18} /> Home</button>
        <h1 className="text-xl font-extrabold text-white">Leaderboard</h1>
      </div>

      <div className="mx-auto max-w-md px-4">
        {/* Country filter (preserved across tabs; not applied to Predictor) */}
        {!isPredictor && (
          <div className="mt-4" data-testid="lb-country-filter">
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm font-semibold outline-none focus:border-amber-400"
              data-testid="lb-country-select"
            >
              <option value="">All Countries</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}

        <div className="mt-3 flex gap-1.5 rounded-xl border border-border bg-card p-1">
          {/* Scope dropdown (Overall / weeks / Unassigned) — replaces the static Cumulative tab */}
          <div className="relative flex-1">
            <button
              onClick={() => { setTab("cumulative"); setScopeOpen((o) => !o); }}
              className={`flex w-full items-center justify-center gap-1 rounded-lg py-2 text-[11px] font-bold ${isCumulative ? "wc-bg-gold text-slate-900" : "text-muted-foreground"}`}
              data-testid="lb-scope-tab"
            >
              <span className="max-w-[88px] truncate">{scopeLabel}</span>
              <ChevronDown size={13} className="shrink-0" />
            </button>
            {scopeOpen && (
              <>
                <div className="fixed inset-0 z-[40]" onClick={() => setScopeOpen(false)} data-testid="lb-scope-backdrop" />
                <div className="absolute left-0 top-full z-[50] mt-1 max-h-72 w-56 overflow-y-auto rounded-xl border border-border bg-card p-1 shadow-xl" data-testid="lb-scope-menu">
                  <button
                    onClick={() => selectScope("overall")}
                    className={`block w-full rounded-lg px-3 py-2 text-left text-sm font-bold ${scope === "overall" ? "wc-gold" : "text-foreground"} hover:bg-secondary`}
                    data-testid="lb-scope-overall"
                  >
                    Overall
                  </button>
                  {weeks.map((w) => (
                    <button
                      key={w.id}
                      onClick={() => selectScope(w.id)}
                      className={`block w-full rounded-lg px-3 py-2 text-left text-sm font-bold ${scope === w.id ? "wc-gold" : "text-foreground"} hover:bg-secondary`}
                      data-testid={`lb-scope-${w.id}`}
                    >
                      {w.week_name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {OTHER_TABS.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} className={`flex-1 rounded-lg py-2 text-[11px] font-bold ${tab === key ? "wc-bg-gold text-slate-900" : "text-muted-foreground"}`} data-testid={`lb-tab-${key}`}>{label}</button>
          ))}
        </div>

        {isPredictor && (
          <button onClick={() => navigate("/football/predictor/history")} className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold wc-gold underline underline-offset-2" data-testid="lb-view-history">
            View my history
          </button>
        )}

        {loading ? <div className="mt-5 h-60 animate-pulse rounded-xl bg-secondary/50" /> : (
          <div className="mt-4 rounded-xl border border-border bg-card p-2">
            {entries.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="lb-empty">{emptyText}</div> : (
              <>
                {!isPredictor && (
                  <div className="grid grid-cols-12 gap-1 px-2 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground" data-testid="lb-header">
                    <span className="col-span-1" />
                    <span className="col-span-4">Team</span>
                    <span className="col-span-2 text-right">Match</span>
                    <span className="col-span-2 text-right">Bonus + Pred</span>
                    <span className="col-span-3 text-right">Total</span>
                  </div>
                )}
                <div className="space-y-1">
                  {entries.map((e) => isPredictor
                    ? <PredictorRow key={e.user_id} e={e} onOpenProfile={setSelectedProfile} />
                    : <StatRow key={e.user_id} e={e} cumulative={isCumulative} onOpenProfile={setSelectedProfile} />)}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {selectedProfile && (
        <FootballProfileModal entry={selectedProfile} onClose={() => setSelectedProfile(null)} />
      )}
    </div>
  );
}
